<script setup lang="ts">
import { useGlobalCounter } from '@/composables/useGlobalCounter';
import Message from 'primevue/message';
import Password from 'primevue/password';
import { useAttrs, type Ref } from 'vue';

defineOptions({
    // disable automatic attr inheritance: we do not want to pass attr to the root element,
    // but to the inner field instead
    inheritAttrs: false,
});

export type KpPasswordFieldProps = {
    label?: string;
    inputId?: string;
    inputClass?: string | object;
    error?: string | null;
};

const value: Ref<any> = defineModel();
const props = withDefaults(defineProps<KpPasswordFieldProps>(), {
    inputId: () => useGlobalCounter('field'),
    inputClass: '',
    error: '',
});

const emit = defineEmits<{
    (e: 'change', value: any): void;
    (e: 'enter'): void;
}>();

const cls = useAttrs().class || null;
const fieldAttrs = { ...useAttrs() };
// Remove attributes for the inner field we do NOT want to pass along:
delete fieldAttrs.id;
delete fieldAttrs.class;
delete fieldAttrs.label;

function onKeypress(e: KeyboardEvent) {
    if (e.key.toLowerCase() === 'enter') {
        emit('enter');
    }
}

function onChange(value: any) {
    emit('change', value);
}
</script>

<template>
    <div :class="`field ${cls}`">
        <label v-if="label" :for="inputId" v-html="label"></label>
        <Password
            :id="inputId"
            v-model="value"
            :class="inputClass"
            v-bind="fieldAttrs"
            @keypress="onKeypress"
            @value-change="onChange"
        >
            <template #header><slot name="header"></slot></template>
            <template #content><slot name="content"></slot></template>
            <template #footer><slot name="footer"></slot></template>
        </Password>
        <Message v-if="error" class="error" size="small" severity="error" variant="simple">{{ error }}</Message>
    </div>
</template>

<style lang="css" scoped>
.error {
    --p-message-text-font-weight: normal;
}
</style>
